import { Box, Container, Stack, Typography, alpha, useTheme } from '@mui/material';
import vmIcon from '@/assets/vm-icon.png';
import { UserMenu } from './UserMenu';

const waveformBars = [18, 32, 14, 38, 24, 44, 16, 28, 40, 22, 36, 18, 30];

type Props = {
  eventName?: string;
};

export function GuestHeader({ eventName }: Props) {
  const theme = useTheme();

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        overflow: 'hidden',
        pt: { xs: 0.75, sm: 1 },
        backgroundImage: `
          linear-gradient(180deg, ${alpha(theme.palette.text.primary, 0.78)} 0%, ${alpha(theme.palette.text.primary, 0.42)} 58%, ${alpha(theme.palette.text.primary, 0)} 100%),
          linear-gradient(118deg, ${alpha(theme.palette.primary.dark, 0.5)} 0 24%, transparent 24% 48%, ${alpha(theme.palette.secondary.dark, 0.28)} 48% 64%, transparent 64%)
        `,
        backdropFilter: 'blur(20px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.secondary.light, 0.18)} 28%, ${alpha(theme.palette.warning.light, 0.14)} 50%, transparent 78%)`,
          transform: 'skewY(-2deg) translateY(-62%)',
          transformOrigin: 'left top',
          height: 46,
        },
      }}
    >
      <Container maxWidth="sm">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            gap: 1.5,
            py: { xs: 0.6, sm: 0.75 },
            position: 'relative',
            borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.065)}`,
          }}
        >
          {/* Logo */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flexShrink: 0 }}>
            <Box
              component="img"
              src={vmIcon}
              alt=""
              aria-hidden="true"
              sx={{
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
                borderRadius: '34% 46% 36% 44%',
                display: 'block',
                transform: 'rotate(-3deg)',
                boxShadow: `0 0 0 1px ${alpha(theme.palette.common.white, 0.12)}, 0 6px 14px ${alpha(theme.palette.secondary.main, 0.14)}`,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="p"
                noWrap
                sx={{ fontSize: { xs: 15, sm: 16 }, lineHeight: 1.05, fontWeight: 900, color: 'common.white' }}
              >
                Vibe Masters
              </Typography>
              {eventName && (
                <Typography
                  component="p"
                  noWrap
                  sx={{
                    mt: 0.35,
                    color: alpha(theme.palette.common.white, 0.55),
                    fontSize: 10.5,
                    lineHeight: 1,
                    fontWeight: 600,
                    maxWidth: { xs: 140, sm: 220 },
                  }}
                >
                  {eventName}
                </Typography>
              )}
            </Box>
          </Stack>

          {/* Right actions */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
            <UserMenu tone="dark" />
          </Stack>
        </Stack>
      </Container>

      {/* Waveform accent */}
      <Box
        aria-hidden="true"
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '3px',
          height: 6,
          maxWidth: 600,
          mx: 'auto',
          px: { xs: 3, sm: 4 },
          overflow: 'hidden',
          opacity: 0.12,
        }}
      >
        {waveformBars.map((h, i) => (
          <Box
            key={i}
            sx={{
              flex: '0 0 auto',
              width: 3,
              height: h * 0.22,
              borderRadius: '2px 2px 0 0',
              background:
                i % 3 === 0
                  ? `linear-gradient(180deg, ${theme.palette.warning.light}, ${theme.palette.secondary.main})`
                  : `linear-gradient(180deg, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
